import { loadRevisionDataThunk } from "@/data-preparation-tool/loading/load-revision-data-thunk";
import { selectRegistrationRevision } from "@/data-preparation-tool/store/revision/revision-entity-selectors";
import { selectUpdatedRevisionEdges } from "@/data-preparation-tool/store/user-edits/data-preparation-user-edit-selectors";
import { AppDispatch, RootState } from "@/store/store";
import { GUID } from "@faro-lotv/foundation";
import { ProjectApi } from "@faro-lotv/service-wires";
import { applyRevision } from "../editing/apply-revision";
import { revisionToEdit } from "../inspect-and-publish/refine-and-publish-utils";
import { clearUserEdits } from "../store/data-preparation-actions";

/**
 * Determines whether a collection of scans should be considered "fixed" based on the count of
 * fixed scans. When all scans are fixed, returns true. When no scans are fixed, returns false.
 * Otherwise, returns true if more than half the scans are fixed (majority rule).
 *
 * @param fixedCount The number of fixed scans.
 * @param totalCount The total number of scans.
 * @returns Whether the scans should be considered fixed.
 */
export function computeFixedStatus(
  fixedCount: number,
  totalCount: number,
): boolean {
  if (fixedCount === totalCount) {
    return true;
  } else if (fixedCount === 0) {
    return false;
  }

  // Toggle to the majority fixed status
  return fixedCount >= totalCount - fixedCount;
}

/**
 * Toggles the fixed status of the given captures.
 *
 * @param dispatch The dispatch function of the store.
 * @param state The current store state.
 * @param projectApiClient The API client to use.
 * @param currentlyFixed Whether the captures are currently fixed.
 * @param captureIds The IDs of the captures to toggle the fixed status of.
 * @param setIsProcessing Callback to update the processing state.
 * @param onSuccess Callback invoked after the operation completes successfully.
 */
export async function toggleFixedStatus(
  dispatch: AppDispatch,
  state: RootState,
  projectApiClient: ProjectApi,
  currentlyFixed: boolean,
  captureIds: GUID[],
  setIsProcessing: (isProcessing: boolean) => void,
  onSuccess?: () => void,
): Promise<void> {
  const currentRevision = selectRegistrationRevision(state);
  if (!currentRevision) return;

  setIsProcessing(true);
  try {
    const newFixedStatus = !currentlyFixed;
    const editRevision = await revisionToEdit(
      currentRevision,
      projectApiClient,
      state,
    );

    await projectApiClient.createOrUpdateScanEntitiesForRegistrationRevision({
      registrationRevisionId: editRevision.id,
      requestBody: captureIds.map((id) => ({ id, isFixed: newFixedStatus })),
    });

    const updatedEdges = selectUpdatedRevisionEdges(state);

    if (updatedEdges.newEdges.length) {
      await projectApiClient.createOrUpdateRegistrationEdges({
        registrationRevisionId: editRevision.id,
        requestBody: updatedEdges.newEdges,
      });
    }

    if (updatedEdges.deletedEdges.size) {
      await projectApiClient.markRegistrationEdgeRevisionsAsDeleted(
        editRevision.id,
        Array.from(updatedEdges.deletedEdges),
      );
    }

    await applyRevision({ revision: editRevision, projectApiClient });
    dispatch(clearUserEdits());

    await dispatch(loadRevisionDataThunk({ projectApiClient }));
    onSuccess?.();
  } finally {
    setIsProcessing(false);
  }
}
